import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search } from "lucide-react";
import DahliasShell from "../components/dahlias/DahliasShell";
import { HeritageSealSilhouette } from "../components/brand/Logos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Names-only collection list: every variety in the final Tuber Audit, once, alphabetically.
 *  Photos, descriptions, dimensions, colour filters and counts are intentionally not shown for now.
 *  (The previous card catalogue is preserved in DahliasCatalogLegacy.jsx.) */
export default function DahliasCatalog() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/catalog/audited-names`).then((r) => setRows(r.data?.varieties || [])).catch(() => setErr("The collection list couldn't load. Please try again shortly."));
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (rows || []).filter((v) => !t || v.name.toLowerCase().includes(t));
  }, [rows, q]);

  const byLetter = useMemo(() => {
    const m = new Map();
    filtered.forEach((v) => { const L = (v.name[0] || "#").toUpperCase(); if (!m.has(L)) m.set(L, []); m.get(L).push(v); });
    return [...m.entries()];
  }, [filtered]);

  return (
    <DahliasShell>
      <section data-testid="catalog-hero" className="bg-[var(--dahlias-cream)] border-b border-[#6E1E3A]/15">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12 sm:py-16 flex flex-col sm:flex-row sm:items-end gap-6">
          <HeritageSealSilhouette className="w-16 h-16 text-[#6E1E3A]/70 shrink-0" />
          <div>
            <div className="font-label text-[10px] tracking-[0.4em] uppercase text-[#6E1E3A]">Heritage Dahlias · The Collection</div>
            <h1 data-testid="catalog-heading" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--dahlias-ink)] mt-2">The named varieties we grow</h1>
            <p className="mt-3 text-sm sm:text-base text-[var(--dahlias-ink)]/70 max-w-2xl" data-testid="catalog-notice">
              Our current collection, listed by name from this season's tuber audit. A listed variety doesn't mean tubers are available to buy right now ,
              the Store shows what's on sale.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <label className="relative block max-w-md mb-8">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6E1E3A]/60" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a variety name" data-testid="catalog-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-full border border-[#6E1E3A]/25 bg-white text-sm focus:outline-none focus:border-[#6E1E3A]" />
        </label>

        {err && <p className="text-sm text-[#A33030]" data-testid="catalog-error">{err}</p>}
        {!rows && !err && <p className="text-sm text-[var(--dahlias-ink)]/60">Loading the collection…</p>}
        {rows && filtered.length === 0 && <p className="text-sm text-[var(--dahlias-ink)]/60" data-testid="catalog-empty">No variety matches “{q}”.</p>}

        <div className="space-y-8" data-testid="catalog-names">
          {byLetter.map(([L, items]) => (
            <div key={L} className="grid grid-cols-[2.5rem_1fr] gap-4">
              <div className="font-serif text-3xl text-[#6E1E3A]/60 leading-none" data-testid={`catalog-letter-${L}`}>{L}</div>
              <ul className="columns-1 sm:columns-2 lg:columns-3 gap-x-8">
                {items.map((v) => (
                  <li key={v.name} className="break-inside-avoid py-1.5 border-b border-dotted border-[#6E1E3A]/15 text-[15px] text-[var(--dahlias-ink)]" data-testid={`catalog-variety-${v.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                    {v.name}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </DahliasShell>
  );
}
